const ORDER_ALPHABET = '-0123456789abcdefghijklmnopqrstuvwxyz';
const BASE_LENGTH = ORDER_ALPHABET.length;
const MIN_DIGIT = 0;
const MAX_DIGIT = BASE_LENGTH - 1;

const charToIndex = new Map<string, number>();
for (let index = 0; index < ORDER_ALPHABET.length; index += 1) {
  charToIndex.set(ORDER_ALPHABET[index]!, index);
}

function charIndex(char: string): number {
  const normalized = /[A-Z]/.test(char) ? char.toLowerCase() : char;
  const index = charToIndex.get(normalized);
  if (index === undefined) throw new Error(`Invalid sort key character: "${char}"`);
  return index;
}

function digitAt(key: string | undefined, position: number, fallback: number): number {
  if (!key || position >= key.length) return fallback;
  return charIndex(key[position]!);
}

function indexToChar(index: number): string {
  if (index < MIN_DIGIT || index > MAX_DIGIT) {
    throw new Error(`Digit index ${index} is outside of supported range`);
  }
  return ORDER_ALPHABET[index]!;
}

function mid(lower?: string, upper?: string): string {
  if (lower && upper && lower >= upper) throw new Error('lower must be less than upper');

  let prefix = '';
  for (let position = 0; ; position += 1) {
    const lowerDigit = digitAt(lower, position, MIN_DIGIT);
    const upperDigit = digitAt(upper, position, BASE_LENGTH);

    if (lowerDigit === upperDigit) {
      prefix += indexToChar(lowerDigit);
      continue;
    }

    const gap = upperDigit - lowerDigit;
    if (gap > 1) return prefix + indexToChar(lowerDigit + Math.floor(gap / 2));
    prefix += indexToChar(lowerDigit);
  }
}

export function generateSortOrder(
  index1?: string,
  bound1: 'lower' | 'upper' = 'lower',
  index2?: string,
  bound2: 'lower' | 'upper' = 'upper',
): string {
  if (index2) {
    if (bound1 === 'lower' && bound2 === 'upper') return mid(index1, index2);
    if (bound1 === 'upper' && bound2 === 'lower') return mid(index2, index1);
  }
  return bound1 === 'lower' ? mid(index1) : mid(undefined, index1);
}

interface BuilderPageLike {
  absolute_layer?: Record<string, unknown>;
  data?: Record<string, unknown>;
  [key: string]: unknown;
}

export function appendMissingFields<T extends BuilderPageLike>(items: T[]): T[] {
  return items.map((item) => ({
    ...item,
    absolute_layer: {
      type: 'absolute_layer',
      fields: [],
      ...item.absolute_layer,
    },
    data: {
      ...item.data,
      visibility: item.data?.visibility ?? true,
    },
  }));
}
